import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Dialog,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAuth } from "context/AuthContext";
import { deleteProvider } from "services/paymentApi";
import BaseModal from "components/BaseModal";
import OnlinePaymentsConfig from "pages/DealerById/components/DealerInfo/components/OnlinePaymentsConfig";
import ProviderAdditionalStepModal from "pages/DealerById/components/DealerInfo/components/OnlinePaymentsConfig/Components/ProviderAdditionalStepModal";
import {
  hasProviderAdditionalStep,
  normalizeProviderAdditionalStep,
} from "pages/DealerById/components/DealerInfo/components/OnlinePaymentsConfig/utils/additionalStepContent";
import type { ProviderAdditionalStep } from "services/paymentApi.types";
import { Provider, AvailableProvider, EditProviderData } from "./types";
import * as Styled from "./styles";
import { ProviderState, ProviderCode, PaymentMethod } from "../../enums";
import { getErrorMessage } from "../../utils/errorHandler";

export interface DealerAcquirersTableProps {
  providers: Provider[];
  onProviderDeleted?: (providerId: string) => void;
  onProviderStatusChanged?: (providerId?: string, newState?: string) => void;
  onProviderAdded?: (
    provider?: Provider,
    meta?: { additionalStepPresented?: boolean },
  ) => void;
  onProviderUpdated?: (provider?: Provider) => void;
  availableProviders?: AvailableProvider[];
  merchantInfo?: { categoryCode: string } | null;
}

interface AcquirerData {
  id: string;
  provider: string;
  status: "Ativo" | "Inativo";
  method: string;
  flags: number;
  lastUpdate: string;
  priority: number | string;
}

const formatMethods = (methods: string[]): string => {
  const methodsMap: Record<string, string> = {
    [PaymentMethod.Credit]: "Crédito",
    [PaymentMethod.Debit]: "Débito",
    [PaymentMethod.Pix]: "Pix",
    [PaymentMethod.PixParcelado]: "Pix Parcelado",
  };
  return methods.map((method) => methodsMap[method] || method).join(", ");
};

const formatDate = (dateString: string): string => {
  if (!dateString) return "";

  const date = new Date(dateString);
  return date.toLocaleDateString("pt-BR");
};

const mapProviderToAcquirerData = (provider: Provider): AcquirerData => {
  return {
    id: provider?.id,
    provider: provider?.name,
    status:
      provider.state.toLowerCase() === ProviderState.Enabled.toLowerCase()
        ? "Ativo"
        : "Inativo",
    method: formatMethods(provider.methods),
    flags: provider.acquirer.brands?.length || 0,
    lastUpdate: formatDate(provider.modifiedAt ?? provider.createdAt),
    priority: provider.priority,
  };
};

const DealerAcquirersTable: React.FC<DealerAcquirersTableProps> = ({
  providers,
  onProviderDeleted,
  onProviderStatusChanged,
  onProviderAdded,
  onProviderUpdated,
  availableProviders = [],
  merchantInfo,
}) => {
  const { t } = useTranslation();
  const { dealerId } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<AcquirerData | null>(
    null,
  );
  const [selectedProviderForModal, setSelectedProviderForModal] =
    useState<Provider | null>(null);
  const [isModalReadOnly, setIsModalReadOnly] = useState(false);
  const [isReviewMode, setIsReviewMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [additionalStepForModal, setAdditionalStepForModal] =
    useState<ProviderAdditionalStep | null>(null);

  const acquirersData: AcquirerData[] = useMemo(() => {
    if (!Array.isArray(providers)) {
      return [];
    }

    const filteredProviders = providers.filter(
      (provider) =>
        provider.code?.toLowerCase() !==
        ProviderCode.LocalPayment.toLowerCase(),
    );

    const mappedProviders = filteredProviders.map(mapProviderToAcquirerData);

    const enabledProviders = mappedProviders.filter(
      (provider) => provider.status === "Ativo",
    );
    const disabledProviders = mappedProviders.filter(
      (provider) => provider.status === "Inativo",
    );

    const sortedEnabledProviders = enabledProviders.sort((a, b) => {
      const priorityA = Number(a.priority);
      const priorityB = Number(b.priority);
      return priorityA - priorityB;
    });

    return [...sortedEnabledProviders, ...disabledProviders];
  }, [providers]);

  const handleView = useCallback(
    (id: string) => {
      const provider = providers.find((p) => p.id === id);
      if (provider) {
        setSelectedProviderForModal(provider);
        setIsModalReadOnly(true);
        setShowProviderModal(true);
      }
    },
    [providers],
  );

  const handleEdit = useCallback(
    (id: string) => {
      const provider = providers.find((p) => p.id === id);
      if (provider) {
        setSelectedProviderForModal(provider);
        setIsModalReadOnly(false);
        setShowProviderModal(true);
      }
    },
    [providers],
  );

  const handleCloseProviderModal = useCallback(() => {
    setShowProviderModal(false);
    setSelectedProviderForModal(null);
    setIsModalReadOnly(false);
    setIsReviewMode(false);
  }, []);

  const handleModalEdit = useCallback(() => {
    setIsModalReadOnly(false);
  }, []);

  const getModalTitle = useCallback(() => {
    if (isReviewMode) {
      return "Revisar dados do novo provedor";
    }
    if (isModalReadOnly) {
      return "Visualizar provedor de pagamento";
    }
    return "Editar provedor de pagamento";
  }, [isReviewMode, isModalReadOnly]);

  const getModalDescription = useCallback(() => {
    if (isReviewMode) {
      return "Revise os dados preenchidos antes de confirmar a adição do provedor";
    }
    return "Cadastre um novo provedor para recebimento de pagamentos";
  }, [isReviewMode]);

  useEffect(() => {
    if (selectedProviderForModal) {
      const updatedProvider = providers.find(
        (p) => p.id === selectedProviderForModal.id,
      );
      if (updatedProvider) {
        setSelectedProviderForModal(updatedProvider);
      }
    }
  }, [providers, selectedProviderForModal]);

  const handleProviderModalSuccess = useCallback(
    (provider?: Provider) => {
      handleCloseProviderModal();
      if (provider) {
        const isEditMode = !!selectedProviderForModal;
        if (isEditMode && onProviderUpdated) {
          onProviderUpdated(provider);
        } else if (!isEditMode && onProviderAdded) {
          onProviderAdded(provider);
        }
      }
    },
    [
      handleCloseProviderModal,
      onProviderAdded,
      onProviderUpdated,
      selectedProviderForModal,
    ],
  );

  const mapProviderToEditFormat = useCallback(
    (provider: Provider): EditProviderData => {
      const availableProvider = availableProviders.find(
        (p) => p.code === provider.code,
      );

      const credentials: Record<string, string> = {};
      if (provider.credentials) {
        Object.keys(provider.credentials).forEach((key) => {
          if (key !== "type") {
            const value = provider.credentials[key];
            if (value) {
              credentials[key] = value;
            }
          }
        });
      }

      const { acquirer } = provider;
      const merchantId = acquirer?.merchantId || "";
      const brandsArray = acquirer?.brands || [];

      const brands = brandsArray.map((brand: string) => ({
        id: brand,
        brand,
        merchantId,
      }));

      return {
        id: provider.id,
        provider: {
          id: availableProvider?.id || "",
        },
        priority: Number(provider.priority),
        merchantId,
        mcc: (() => {
          const categoryCode = merchantInfo?.categoryCode;
          return categoryCode &&
            categoryCode !== "0" &&
            Number(categoryCode) !== 0
            ? categoryCode
            : "";
        })(),
        isActive:
          provider.state.toLowerCase() === ProviderState.Enabled.toLowerCase(),
        merchantKey: "",
        brands,
        credentials,
      };
    },
    [availableProviders, merchantInfo],
  );

  const handleOpenAdditionalStep = useCallback(
    (id: string) => {
      const provider = providers.find((item) => item.id === id);
      if (!provider || !hasProviderAdditionalStep(provider.additionalSteps)) {
        return;
      }

      setAdditionalStepForModal(
        normalizeProviderAdditionalStep(provider.additionalSteps),
      );
    },
    [providers],
  );

  const handleCloseAdditionalStepModal = useCallback(() => {
    setAdditionalStepForModal(null);
  }, []);

  const handleDelete = useCallback(
    (id: string) => {
      const provider = acquirersData.find((acq) => acq.id === id);
      if (provider) {
        setSelectedProvider(provider);
        setShowDeleteModal(true);
      }
    },
    [acquirersData],
  );

  const handleCloseDeleteModal = useCallback(() => {
    if (!isDeleting) {
      setShowDeleteModal(false);
      setSelectedProvider(null);
    }
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedProvider || !dealerId) return;

    setIsDeleting(true);
    try {
      await deleteProvider({
        merchantId: dealerId,
        providerId: selectedProvider.id,
      });

      toast("Provedor excluído com sucesso!", { type: "success" });
      setShowDeleteModal(false);
      const deletedProviderId = selectedProvider?.id;
      setSelectedProvider(null);
      if (deletedProviderId) {
        onProviderDeleted?.(deletedProviderId);
      }
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(
        error,
        "Não foi possível excluir o provedor. Tente novamente!",
      );
      toast(errorMessage, { type: "error" });
    } finally {
      setIsDeleting(false);
    }
  }, [selectedProvider, dealerId, onProviderDeleted]);

  return (
    <Styled.Container>
      <TableContainer component={Paper} elevation={0}>
        <Styled.StyledTable>
          <TableHead>
            <TableRow>
              <Styled.StyledTableCellID>
                <Typography variant="body2" fontWeight={500} />
              </Styled.StyledTableCellID>
              <Styled.StyledTableCellProvider>
                <Typography variant="body2" fontWeight={500}>
                  {t("provider")}
                </Typography>
              </Styled.StyledTableCellProvider>
              <Styled.StyledTableCellStatus>
                <Typography variant="body2" fontWeight={500}>
                  {t("status")}
                </Typography>
              </Styled.StyledTableCellStatus>
              <Styled.StyledTableCellMethod>
                <Typography variant="body2" fontWeight={500}>
                  {t("method")}
                </Typography>
              </Styled.StyledTableCellMethod>
              <Styled.StyledTableCellFlags>
                <Typography variant="body2" fontWeight={500}>
                  {t("flags")}
                </Typography>
              </Styled.StyledTableCellFlags>
              <Styled.StyledTableCellLastUpdate>
                <Typography variant="body2" fontWeight={500}>
                  {t("lastUpdate")}
                </Typography>
              </Styled.StyledTableCellLastUpdate>
              <Styled.StyledTableCellActions>
                <Typography variant="body2" fontWeight={500}>
                  {t("actions")}
                </Typography>
              </Styled.StyledTableCellActions>
            </TableRow>
          </TableHead>
          <TableBody>
            {acquirersData.map((acquirer) => {
              const providerRecord = providers.find(
                (item) => item.id === acquirer.id,
              );
              const showAdditionalStepAction =
                providerRecord &&
                hasProviderAdditionalStep(providerRecord.additionalSteps);

              return (
                <TableRow key={acquirer.id}>
                  <Styled.StyledTableCellWithPadding>
                    <Typography variant="body2">
                      {acquirer.status === "Ativo" ? acquirer.priority : "-"}
                    </Typography>
                  </Styled.StyledTableCellWithPadding>
                  <Styled.StyledTableCellWithPadding>
                    <Typography variant="body2">{acquirer.provider}</Typography>
                  </Styled.StyledTableCellWithPadding>
                  <Styled.StyledTableCellWithPadding>
                    <Typography variant="body2">{acquirer.status}</Typography>
                  </Styled.StyledTableCellWithPadding>
                  <Styled.StyledTableCellWithPadding>
                    <Typography variant="body2">{acquirer.method}</Typography>
                  </Styled.StyledTableCellWithPadding>
                  <Styled.StyledTableCellWithPadding>
                    <Typography variant="body2">
                      {acquirer.flags.toString()}
                    </Typography>
                  </Styled.StyledTableCellWithPadding>
                  <Styled.StyledTableCellWithPadding>
                    <Typography variant="body2">
                      {acquirer.lastUpdate}
                    </Typography>
                  </Styled.StyledTableCellWithPadding>
                  <Styled.StyledTableCellWithPadding>
                    <Styled.ActionsBox>
                      <Styled.ActionIconButton
                        size="small"
                        onClick={() => handleView(acquirer.id)}
                      >
                        <Styled.VisibilityIconStyled />
                      </Styled.ActionIconButton>
                      <Styled.ActionIconButton
                        size="small"
                        onClick={() => handleEdit(acquirer.id)}
                      >
                        <Styled.EditIconStyled />
                      </Styled.ActionIconButton>
                      <Styled.ActionIconButton
                        size="small"
                        onClick={() => handleDelete(acquirer.id)}
                      >
                        <Styled.DeleteIconStyled />
                      </Styled.ActionIconButton>
                      {showAdditionalStepAction && (
                        <Styled.ActionIconButton
                          size="small"
                          onClick={() => handleOpenAdditionalStep(acquirer.id)}
                        >
                          <Styled.LaunchIconStyled />
                        </Styled.ActionIconButton>
                      )}
                    </Styled.ActionsBox>
                  </Styled.StyledTableCellWithPadding>
                </TableRow>
              );
            })}
          </TableBody>
        </Styled.StyledTable>
      </TableContainer>

      <Styled.FooterContainer>
        <Typography variant="caption">
          {t("totalItems", { count: acquirersData?.length || 0 })}
        </Typography>
        <Styled.PaginationBox>
          <IconButton size="small" disabled>
            <Styled.ChevronLeftIconStyled />
          </IconButton>
          <IconButton size="small" disabled>
            <Styled.ChevronRightIconStyled />
          </IconButton>
        </Styled.PaginationBox>
      </Styled.FooterContainer>

      {additionalStepForModal && (
        <ProviderAdditionalStepModal
          open={Boolean(additionalStepForModal)}
          additionalStep={additionalStepForModal}
          onClose={handleCloseAdditionalStepModal}
        />
      )}

      <BaseModal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        title="Excluir"
        cancellationText="Cancelar"
        confirmationText={isDeleting ? "Excluindo..." : "Sim, desejo excluir"}
        cancellationCallback={handleCloseDeleteModal}
        confirmationCallback={handleConfirmDelete}
      >
        <Styled.ModalContent>
          <Styled.ModalTypography variant="h6">
            Você tem certeza que deseja excluir o provedor?
          </Styled.ModalTypography>
          <Styled.ImportantNotice>
            <Styled.ModalInfoIcon />
            <Styled.ModalImportantText component="span">
              <Styled.ModalImportantTextBold component="span">
                Importante:
              </Styled.ModalImportantTextBold>{" "}
              Esta ação é irreversível. O provedor será removido permanentemente
              e não poderá ser recuperado.
            </Styled.ModalImportantText>
          </Styled.ImportantNotice>
        </Styled.ModalContent>
      </BaseModal>

      {showProviderModal && (
        <Dialog
          open={showProviderModal}
          onClose={handleCloseProviderModal}
          maxWidth="lg"
          fullWidth
        >
          <Styled.DialogTitleStyled>
            <Styled.DialogTitleContainer>
              <Typography variant="h6">{getModalTitle()}</Typography>
              <Styled.DialogSubtitle variant="body1">
                {getModalDescription()}
              </Styled.DialogSubtitle>
            </Styled.DialogTitleContainer>
            <Styled.CloseButton onClick={handleCloseProviderModal} size="small">
              <CloseIcon />
            </Styled.CloseButton>
          </Styled.DialogTitleStyled>

          <Styled.DialogContentStyled>
            <OnlinePaymentsConfig
              mode="EDIT"
              existingProvider={
                selectedProviderForModal
                  ? mapProviderToEditFormat(selectedProviderForModal)
                  : null
              }
              onCancel={handleCloseProviderModal}
              onEditClick={handleModalEdit}
              onReviewModeChange={setIsReviewMode}
              onSuccess={handleProviderModalSuccess}
              onProviderDeleted={onProviderDeleted}
              onProviderStatusChanged={onProviderStatusChanged}
              onProviderUpdated={onProviderUpdated}
              merchantInfo={merchantInfo}
              availableProviders={availableProviders}
              dealerProviders={providers}
              readOnly={isModalReadOnly}
            />
          </Styled.DialogContentStyled>
        </Dialog>
      )}
    </Styled.Container>
  );
};

export default DealerAcquirersTable;
